import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CalendarClock, Plus, Clock, User, Stethoscope, CheckCircle, XCircle, 
  AlertTriangle, ChevronLeft, ChevronRight, PlayCircle, Edit, Search,
  UserCheck, Ban, CalendarX
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPOINTMENT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'review', label: 'Review' }
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  checked_in: 'bg-amber-100 text-amber-800 border-amber-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  no_show: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200'
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  checked_in: 'Checked In',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled'
};

const TYPE_COLORS = {
  consultation: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  follow_up: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  procedure: 'bg-purple-50 text-purple-700 border-purple-200',
  review: 'bg-teal-50 text-teal-700 border-teal-200'
};

const Appointments = () => {
  const { user, isOwner, isDoctor, isReception, canEditClinical } = useAuth();
  const canManageAppointments = isOwner || isReception;
  
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Metrics (owner only)
  const [metrics, setMetrics] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    patient_id: '',
    provider_id: '',
    date: selectedDate,
    start_time: '09:00',
    end_time: '09:30',
    appointment_type: 'consultation',
    notes: ''
  });
  
  const [statusData, setStatusData] = useState({
    status: '',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = { date: selectedDate };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const [appointmentsRes, patientsRes, doctorsRes] = await Promise.all([
        axios.get(`${API}/appointments`, { params }),
        axios.get(`${API}/patients`),
        axios.get(`${API}/doctors`)
      ]);
      
      setAppointments(appointmentsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      
      // Fetch metrics for owner
      if (isOwner) {
        const metricsRes = await axios.get(`${API}/appointments/metrics/summary`, { params: { period: 'today' } });
        setMetrics(metricsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter, isOwner]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/appointments`, formData);
      setAppointments([...appointments, response.data]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Appointment created successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAppointment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/appointments/${selectedAppointment.id}`, formData);
      setAppointments(appointments.map(a => a.id === selectedAppointment.id ? response.data : a));
      setShowEditModal(false);
      setSelectedAppointment(null);
      toast.success('Appointment updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/appointments/${selectedAppointment.id}/status`, statusData);
      setAppointments(appointments.map(a => a.id === selectedAppointment.id ? response.data : a));
      setShowStatusModal(false);
      setSelectedAppointment(null);
      toast.success(`Appointment marked as ${STATUS_LABELS[statusData.status]}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEncounter = async (appointment) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/appointments/${appointment.id}/start-encounter`);
      toast.success('Encounter started');
      // Navigate to encounter detail
      window.location.href = `/encounters/${response.data.id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start encounter');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      patient_id: appointment.patient_id,
      provider_id: appointment.provider_id,
      date: appointment.date,
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      appointment_type: appointment.appointment_type,
      notes: appointment.notes || ''
    });
    setShowEditModal(true);
  };

  const openStatusModal = (appointment, newStatus) => {
    setSelectedAppointment(appointment);
    setStatusData({ status: newStatus, reason: '' });
    setShowStatusModal(true);
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      provider_id: '',
      date: selectedDate,
      start_time: '09:00',
      end_time: '09:30',
      appointment_type: 'consultation',
      notes: ''
    });
  };

  const filteredAppointments = appointments.filter(apt => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return apt.patient_name.toLowerCase().includes(query) || 
             apt.provider_name.toLowerCase().includes(query);
    }
    return true;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="appointments-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">{isDoctor ? 'My Schedule' : 'Appointments'}</h1>
            <p className="text-slate-600 mt-1">
              {isDoctor ? 'Your daily patient schedule' : 'Manage clinic appointments'}
            </p>
          </div>
          {canManageAppointments && (
            <Button
              onClick={() => {
                resetForm();
                setFormData(prev => ({ ...prev, date: selectedDate }));
                setShowCreateModal(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="create-appointment-btn"
            >
              <Plus className="w-4 h-4" />
              New Appointment
            </Button>
          )}
        </div>

        {/* Metrics for Owner */}
        {isOwner && metrics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="card p-4">
              <p className="text-sm text-slate-600">Scheduled</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.scheduled}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-600">Checked In</p>
              <p className="text-2xl font-bold text-amber-600">{metrics.checked_in}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{metrics.completed}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-600">No Shows</p>
              <p className="text-2xl font-bold text-red-600">{metrics.no_show}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-600">Cancelled</p>
              <p className="text-2xl font-bold text-slate-600">{metrics.cancelled}</p>
            </div>
          </div>
        )}

        {/* Date Navigation & Filters */}
        <div className="card mb-6">
          <div className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => handleDateChange(-1)} className="border-2">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-40"
                />
                <p className="text-sm text-slate-600 mt-1">{formatDate(selectedDate)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleDateChange(1)} className="border-2">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                className="border-2"
              >
                Today
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search patient or doctor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="checked_in">Checked In</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="card" data-testid="appointments-list">
          <div className="p-4 border-b-2 border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-5 h-5" />
              Appointments ({filteredAppointments.length})
            </h2>
          </div>
          
          {filteredAppointments.length > 0 ? (
            <div className="divide-y-2 divide-slate-100">
              {filteredAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="p-4 hover:bg-slate-50"
                  data-testid={`appointment-row-${appointment.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Time */}
                      <div className="w-24 text-center">
                        <p className="font-semibold text-slate-900">
                          {appointment.start_time}
                        </p>
                        <p className="text-xs text-slate-500">
                          to {appointment.end_time}
                        </p>
                      </div>
                      
                      {/* Patient Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-semibold text-slate-900">{appointment.patient_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                          <Stethoscope className="w-3 h-3" />
                          <span>Dr. {appointment.provider_name}</span>
                        </div>
                      </div>
                      
                      {/* Type & Status Badges */}
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${TYPE_COLORS[appointment.appointment_type]}`}>
                          {APPOINTMENT_TYPES.find(t => t.value === appointment.appointment_type)?.label}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${STATUS_COLORS[appointment.status]}`}>
                          {STATUS_LABELS[appointment.status]}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {/* Check In - Reception/Owner, only for scheduled */}
                      {canManageAppointments && appointment.status === 'scheduled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatusModal(appointment, 'checked_in')}
                          className="border-2 text-amber-600 hover:bg-amber-50"
                          data-testid={`checkin-btn-${appointment.id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Check In
                        </Button>
                      )}
                      
                      {/* Start Encounter - Doctor/Owner, only for checked_in */}
                      {canEditClinical && appointment.status === 'checked_in' && !appointment.encounter_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartEncounter(appointment)}
                          className="border-2 text-green-600 hover:bg-green-50"
                          disabled={saving}
                          data-testid={`start-encounter-btn-${appointment.id}`}
                        >
                          <PlayCircle className="w-4 h-4 mr-1" />
                          Start Encounter
                        </Button>
                      )}
                      
                      {/* Complete - Doctor/Owner, only for checked_in */}
                      {canEditClinical && appointment.status === 'checked_in' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatusModal(appointment, 'completed')}
                          className="border-2 text-green-600 hover:bg-green-50"
                          data-testid={`complete-btn-${appointment.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      
                      {/* No Show - Doctor/Owner, only for scheduled */}
                      {canEditClinical && appointment.status === 'scheduled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatusModal(appointment, 'no_show')}
                          className="border-2 text-red-600 hover:bg-red-50"
                          data-testid={`noshow-btn-${appointment.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          No Show
                        </Button>
                      )}
                      
                      {/* Cancel - Reception/Owner, only for scheduled */}
                      {canManageAppointments && appointment.status === 'scheduled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatusModal(appointment, 'cancelled')}
                          className="border-2 text-slate-600 hover:bg-slate-100"
                          data-testid={`cancel-btn-${appointment.id}`}
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                      
                      {/* Edit - Reception/Owner, only for scheduled/checked_in */}
                      {canManageAppointments && ['scheduled', 'checked_in'].includes(appointment.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(appointment)}
                          className="border-2"
                          data-testid={`edit-btn-${appointment.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Notes */}
                  {appointment.notes && (
                    <div className="mt-2 ml-28 text-sm text-slate-600 bg-slate-50 rounded p-2">
                      {appointment.notes}
                    </div>
                  )}
                  
                  {/* Linked Encounter */}
                  {appointment.encounter_id && (
                    <div className="mt-2 ml-28">
                      <a 
                        href={`/encounters/${appointment.encounter_id}`}
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Stethoscope className="w-3 h-3" />
                        View Encounter
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CalendarX className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No appointments for this date</p>
              <p className="text-sm text-slate-500">
                {canManageAppointments ? 'Click "New Appointment" to schedule one' : 'Check another date'}
              </p>
            </div>
          )}
        </div>

        {/* Create Appointment Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-lg border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-blue-600" />
                New Appointment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAppointment} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="patient_id">Patient</Label>
                <Select
                  value={formData.patient_id}
                  onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                >
                  <SelectTrigger data-testid="patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="provider_id">Doctor</Label>
                <Select
                  value={formData.provider_id}
                  onValueChange={(value) => setFormData({ ...formData, provider_id: value })}
                >
                  <SelectTrigger data-testid="doctor-select">
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        Dr. {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="start_time">Start Time</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                    data-testid="start-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end_time">End Time</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                    data-testid="end-time-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="appointment_type">Type</Label>
                <Select
                  value={formData.appointment_type}
                  onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  data-testid="notes-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving || !formData.patient_id || !formData.provider_id}
                  data-testid="save-appointment-btn"
                >
                  {saving ? 'Creating...' : 'Create Appointment'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Appointment Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-lg border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                Edit Appointment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateAppointment} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="edit_patient_id">Patient</Label>
                <Select
                  value={formData.patient_id}
                  onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit_provider_id">Doctor</Label>
                <Select
                  value={formData.provider_id}
                  onValueChange={(value) => setFormData({ ...formData, provider_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        Dr. {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit_date">Date</Label>
                  <Input
                    id="edit_date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_start_time">Start Time</Label>
                  <Input
                    id="edit_start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_end_time">End Time</Label>
                  <Input
                    id="edit_end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit_appointment_type">Type</Label>
                <Select
                  value={formData.appointment_type}
                  onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit_notes">Notes (optional)</Label>
                <Input
                  id="edit_notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Status Update Modal */}
        <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                {statusData.status === 'checked_in' && <UserCheck className="w-5 h-5 text-amber-600" />}
                {statusData.status === 'completed' && <CheckCircle className="w-5 h-5 text-green-600" />}
                {statusData.status === 'no_show' && <XCircle className="w-5 h-5 text-red-600" />}
                {statusData.status === 'cancelled' && <Ban className="w-5 h-5 text-slate-600" />}
                {statusData.status === 'checked_in' && 'Check In Patient'}
                {statusData.status === 'completed' && 'Complete Appointment'}
                {statusData.status === 'no_show' && 'Mark as No Show'}
                {statusData.status === 'cancelled' && 'Cancel Appointment'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleStatusUpdate} className="space-y-4 pt-4">
              {selectedAppointment && (
                <div className="bg-slate-50 rounded-lg p-3 border">
                  <p className="font-medium text-slate-900">{selectedAppointment.patient_name}</p>
                  <p className="text-sm text-slate-600">
                    {selectedAppointment.date} at {selectedAppointment.start_time}
                  </p>
                </div>
              )}
              
              {(statusData.status === 'no_show' || statusData.status === 'cancelled') && (
                <div>
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Input
                    id="reason"
                    value={statusData.reason}
                    onChange={(e) => setStatusData({ ...statusData, reason: e.target.value })}
                    placeholder="Enter reason..."
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={`flex-1 text-white ${
                    statusData.status === 'checked_in' ? 'bg-amber-600 hover:bg-amber-700' :
                    statusData.status === 'completed' ? 'bg-green-600 hover:bg-green-700' :
                    statusData.status === 'no_show' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-slate-600 hover:bg-slate-700'
                  }`}
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Confirm'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Appointments;
